import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStreamAuth } from '../../hooks/useStreamAuth';
import { useProfile } from '../../contexts/ProfileContext';
import { useAppLoad } from '../../contexts/AppLoadContext';
import LoadingSpinner from './LoadingSpinner';
import spotlightService from '../../services/SpotlightService';

const ProfileRedirectHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading: authLoading } = useStreamAuth();
  const { loading: appLoadLoading, needsProfileSelection: appNeedsProfileSelection } = useAppLoad();
  const {
    profiles,
    currentProfile,
    hasLoadedProfiles,
    isLoading: profileLoading,
    error: profileError,
    loadProfiles,
    needsProfileSelection,
    isSwitchingProfiles,
  } = useProfile();

  // Load profiles immediately when user is authenticated (don't wait for appload)
  // CRITICAL: Wait for SpotlightService initialization before loading profiles
  // This ensures client token is ready before profile API calls (especially important for Safari)
  useEffect(() => {
    const initializeAndLoadProfiles = async () => {
      if (isAuthenticated && user && !hasLoadedProfiles && !profileLoading) {
        console.log('🔧 ProfileRedirectHandler: Waiting for SpotlightService initialization...');
        
        // Wait for SpotlightService to finish initialization
        // This ensures the client token from Auth0 is properly set before we try to load profiles
        await spotlightService.initializationPromise;
        
        console.log('✅ ProfileRedirectHandler: SpotlightService initialized, loading profiles...');
        loadProfiles(false); // Not a manual retry
      }
    };
    
    initializeAndLoadProfiles();
  }, [isAuthenticated, user, hasLoadedProfiles, profileLoading, loadProfiles]);

  // Handle profile loading errors with retry logic (with circuit breaker)
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  useEffect(() => {
    if (profileError && !hasLoadedProfiles && !profileLoading && retryCount < maxRetries) {
      // Retry after a delay, but only if we haven't loaded profiles yet
      const retryTimer = setTimeout(() => {
        if (!hasLoadedProfiles && !profileLoading && retryCount < maxRetries) {
          console.warn(`ProfileRedirectHandler: Retrying profile load (attempt ${retryCount + 1}/${maxRetries})`);
          setRetryCount(prev => prev + 1);
          loadProfiles(false); // Not a manual retry
        }
      }, 5000 * (retryCount + 1)); // Exponential backoff: 5s, 10s, 15s

      return () => clearTimeout(retryTimer);
    } else if (retryCount >= maxRetries) {
      console.error('ProfileRedirectHandler: Max retries reached, stopping profile load attempts');
    }
  }, [profileError, hasLoadedProfiles, profileLoading, loadProfiles, retryCount]);

  // Handle profile-based redirects
  useEffect(() => {
    // Don't redirect if still loading auth or profiles
    if (authLoading || profileLoading) {
      return;
    }

    // Don't redirect if not authenticated
    if (!isAuthenticated) {
      return;
    }

    // Don't redirect if we haven't loaded profiles yet
    if (!hasLoadedProfiles) {
      return;
    }

    // Don't redirect if already on profile-related pages
    if (location.pathname === '/profiles') {
      return;
    }

    // Don't redirect if user is explicitly switching profiles
    if (isSwitchingProfiles) {
      return;
    }

    let redirectTo = null;
    if (
      (location.pathname === '/' && location.search.length) ||
      location.pathname !== '/profiles'
    ) {
      redirectTo = '?after_selection=' + encodeURIComponent(location.pathname + location.search);
    }

    // Redirect to profile selection if needed
    if (needsProfileSelection()) {
      navigate('/profiles' + (redirectTo || ''), { replace: true });
      return;
    }

    // If user has profiles but no current profile selected, redirect to profiles
    // But only if they're not already on the profiles page (to avoid adding redirect params when switching)
    if (profiles.length > 0 && !currentProfile && location.pathname !== '/profiles') {
      navigate('/profiles' + (redirectTo || ''), { replace: true });
      return;
    }
  }, [
    authLoading,
    profileLoading,
    isAuthenticated,
    hasLoadedProfiles,
    location.pathname,
    needsProfileSelection,
    profiles.length,
    currentProfile,
    navigate,
    isSwitchingProfiles,
  ]);

  // Show loading while checking authentication
  if (authLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // If AppLoadContext indicates we need profile selection, show loading
  // BUT allow the profiles page to render if we're already on it
  if (appNeedsProfileSelection && location.pathname !== '/profiles') {
    return <LoadingSpinner message="Loading profiles..." />;
  }

  // If authenticated but profiles are still loading, show loading
  if (isAuthenticated && (profileLoading || !hasLoadedProfiles)) {
    return <LoadingSpinner message="Loading profiles..." />;
  }

  // If authenticated and profiles are loaded, but need profile selection, show loading
  // BUT allow the profiles page to render if we're already on it
  if (
    isAuthenticated &&
    hasLoadedProfiles &&
    needsProfileSelection() &&
    location.pathname !== '/profiles'
  ) {
    return <LoadingSpinner message="Selecting profile..." />;
  }

  // If authenticated and profiles are loaded, but no current profile, show loading
  // BUT allow the profiles page to render if we're already on it
  if (
    isAuthenticated &&
    hasLoadedProfiles &&
    profiles.length > 0 &&
    !currentProfile &&
    location.pathname !== '/profiles'
  ) {
    return <LoadingSpinner message="Selecting profile..." />;
  }

  // Render children once everything is loaded and checked
  return children;
};

export default ProfileRedirectHandler;
